import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDarkMode } from "../../contexts/ThemeContext";
import Card from "../ui/Card";
import {
  faList,
  faClock,
  faCheckCircle,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { getTimeAgo } from "../../utils/dateUtils";

// counter cards component for Allocate page
const CounterCards = ({ counters, mostRecentRequest, isLoading = false, justUpdated = false }) => {
  const { t } = useTranslation();
  const { darkMode } = useDarkMode();

  // extract values using the CORRECT property names from useAllocateData hook
  // hook returns: { requests, pending, allocated }
  const totalRequests = counters?.requests ?? 0;
  const pending = counters?.pending ?? 0;
  const allocated = counters?.allocated ?? 0;

  // use the same compact time format as RequestCard (e.g., "2h ago", "3d ago")
  let latestRequestTime = t("N/A");
  let latestRequestCompany = t("No recent requests");

  if (mostRecentRequest) {
    // try created_at first, fallback to date_modified
    const dateToUse =
      mostRecentRequest.created_at || mostRecentRequest.date_modified;

    if (dateToUse) {
      const timeAgo = getTimeAgo(dateToUse);
      if (timeAgo) {
        latestRequestTime = timeAgo;
      }
    }

    if (mostRecentRequest.receiver_company) {
      latestRequestCompany = mostRecentRequest.receiver_company;
    }
  }

  // define counter cards with color classes
  const counterCards = [
    {
      icon: faList,
      value: totalRequests,
      title: t("Total Requests"),
      description: t("All material requests"),
      colorClass: "counter-blue",
    },
    {
      icon: faClock,
      value: pending,
      title: t("Pending"),
      description: t("Awaiting allocation"),
      colorClass: "counter-amber",
    },
    {
      icon: faCheckCircle,
      value: allocated,
      title: t("Allocated"),
      description: t("Successfully allocated"),
      colorClass: "counter-green",
    },
    {
      icon: faCalendarAlt,
      value: latestRequestTime,
      title: t("Latest Request"),
      description: latestRequestCompany,
      colorClass: "counter-purple",
    },
  ];

  return (
    <div className="counter-cards-container">
      {counterCards.map((card, index) => (
        <Card
          key={index}
          icon={card.icon}
          value={card.value}
          title={card.title}
          description={card.description}
          variant="counter"
          colorClass={card.colorClass}
          darkMode={darkMode}
          isLoading={isLoading}
          justUpdated={justUpdated}
        />
      ))}
    </div>
  );
};

CounterCards.propTypes = {
  counters: PropTypes.shape({
    requests: PropTypes.number,
    pending: PropTypes.number,
    allocated: PropTypes.number,
  }).isRequired,
  mostRecentRequest: PropTypes.object,
  isLoading: PropTypes.bool,
  justUpdated: PropTypes.bool,
};

export default CounterCards;
